/**
 * @ward3/frontend-shared/authApi — fonctions pures async pour /auth/*.
 *
 * Extrait 2026-07-06 (#17-A revue 2026-06). Chaque fonction prend un
 * client `api` en 1er paramètre → testable indépendamment (mock axios
 * dans un test unitaire, pas d'état global à réinitialiser).
 *
 * Les 2 apps (SOC + admin) appellent exactement ces fonctions ; seul
 * l'AuthContext qui les wire diffère (SOC a un scopeOverride state en
 * plus).
 */

/**
 * Tente un silent refresh via `POST /auth/refresh`. Le cookie HttpOnly
 * `refresh_token` est envoyé automatiquement par le browser
 * (`withCredentials: true` sur le client).
 *
 * @param {import("axios").AxiosInstance} api
 * @returns {Promise<{ access_token: string }>}
 */
export async function postRefresh(api) {
  const { data } = await api.post("/auth/refresh");
  return data;
}

/**
 * Login classique — accepte optionnels `mssp_slug` / `tenant_slug` pour
 * les setups multi-tenant où l'utilisateur passe par un sous-domaine
 * (SOC uniquement, admin ignore ces champs).
 *
 * @param {import("axios").AxiosInstance} api
 * @param {string} email
 * @param {string} password
 * @param {{ mssp_slug?: string, tenant_slug?: string }} [opts]
 * @returns {Promise<{ access_token: string }>}
 */
export async function postLogin(api, email, password, opts = {}) {
  const body = { email, password };
  if (opts.mssp_slug) body.mssp_slug = opts.mssp_slug;
  if (opts.tenant_slug) body.tenant_slug = opts.tenant_slug;
  const { data } = await api.post("/auth/login", body);
  return data;
}

/**
 * Logout best-effort. Le call peut échouer (token expiré, réseau) — le
 * caller doit **quand même** reset son state local. C'est pour ça que
 * cette fonction ne throw pas ; elle retourne un `{ ok: boolean }`.
 *
 * @param {import("axios").AxiosInstance} api
 * @returns {Promise<{ ok: boolean }>}
 */
export async function postLogout(api) {
  try {
    await api.post("/auth/logout");
    return { ok: true };
  } catch {
    return { ok: false };
  }
}

/**
 * Récupère les champs user non portés par le JWT (email, locale). Le
 * JWT est immuable jusqu'au prochain refresh (cf. plan i18n), donc on
 * complète ces champs via un GET séparé après chaque `decodeAndSet`.
 *
 * @param {import("axios").AxiosInstance} api
 * @returns {Promise<{ email: string, locale: string }>}
 */
export async function getMe(api) {
  const { data } = await api.get("/auth/me");
  return data;
}

/**
 * PATCH la préférence de langue serveur. Source de vérité = le retour
 * (le server peut normaliser / rejeter la valeur).
 *
 * @param {import("axios").AxiosInstance} api
 * @param {string} locale
 * @returns {Promise<{ locale: string }>}
 */
export async function patchLocale(api, locale) {
  const { data } = await api.patch("/auth/me/preferences", { locale });
  return data;
}
